// F04 — AI-brief «for i dag»
// Proaktiv dagsmodul: vær, sollys, dagens tips, tipping, huskeliste.
// Cache per dag i localStorage så Claude bare kalles én gang per dag.

import { DAYS, PACKING } from '../data.js';
import { esc } from '../utils.js';
import { getOfflineState, formatStorage, nextOfflineMilestone } from '../offline.js';
import { getHeartState } from '../follow.js';
import { getAuroraOutlook } from '../aurora.js';
import { getDayWeather, formatBadge } from '../weather.js';
import { fillTimelineWeather } from './timeline.js';
import { getPortTides, hasTideData, formatTideLine } from '../tides.js';
import { getDailyTip } from '../brief.js';
import { sunForTripDay, formatSunBadge } from '../sun.js';
import { getRates, peekRates, ageLabel } from '../rates.js';
import { topPreflightAction } from '../preflight.js';

const DEPARTURE = new Date('2026-07-17T13:50:00+02:00');

// Lokal tipping-skikk per fase (USD/CAD)
const TIPS = {
  fly:      { pct:'15–20 %', note:'Vancouver — standard nordamerikansk' },
  city:     { pct:'15–20 %', note:'Restaurant + taxi · CAD' },
  cruise:   { pct:'Inkl.',   note:'Auto-gratuity allerede trukket' },
  alaska:   { pct:'15–20 %', note:'Guider på utflukter · USD' },
  car:      { pct:'15–18 %', note:'BC/Alberta · CAD' },
  mountain: { pct:'15–18 %', note:'Hotell + restaurant · CAD' },
};

// Lokalt huske-tips per fase
const REMINDERS = {
  fly:      'Pass + reisedokumenter i håndbagasje',
  city:     'Compass-kort til Skytrain',
  cruise:   'SeaPass + pass før hver utflukt',
  alaska:   'Lag-på-lag · regnjakke',
  car:      'Internasjonal førerkort + AVIS-bekreftelse',
  mountain: 'Bjørnespray + myggspray',
};

function daysUntilDeparture(){
  return Math.ceil((DEPARTURE - new Date()) / 86400000);
}

function ymd(d){
  return d.toISOString().slice(0,10);
}

function getCachedTip(key){
  try{
    const raw = localStorage.getItem('brief:' + key);
    if(!raw) return null;
    const { tip, ts } = JSON.parse(raw);
    // Cache holder i 24 timer
    if(Date.now() - ts > 86400000) return null;
    return tip;
  }catch{ return null; }
}

function setCachedTip(key, tip){
  try{
    localStorage.setItem('brief:' + key, JSON.stringify({ tip, ts: Date.now() }));
  }catch{}
}

// Statiske fallback-tips per dag — brukes til Claude-integrasjon er på plass (kommer i senere fase)
const STATIC_TIPS = {
  0:  'Online check-in Icelandair åpner 24 t før — book midtgangsplass for benstrekk på 7t 45m-etappen.',
  1:  'Stanley Park Seawall er på sitt fineste tidlig morgen — Granville Island åpner kl. 09.',
  2:  'Embarkering åpner 12:00, men køen er kortest etter 13:30. Pakk kjøkkenutstyr i håndbagasje siden lugarbagasje kan komme sent.',
  3:  'Sea Day — bestill Persian Garden-spa nå mens det er ledig. Captain\'s Welcome i Theater kl. 21.',
  4:  'Icy Strait Point: Whales & Brown Bear-tur starter 14:25. Ta varmt tøy — temperaturen på catamaran-dekk er 8–12 °C selv i juli.',
  5:  'Skagway: White Pass-toget krever pass — alle tre passene må være med fra lugaren før avgang 07:30.',
  6:  'Helikopter til Mendenhall — maks 113 kg per person. Ingen GoPro-stenger. Vekttest på dekk før utflukt.',
  7:  'Ketchikan: Creek Street + Totem Heritage Center hvis det regner. Salmon-lunsj på Bar Harbor.',
  8:  'Siste cruisedag — pakk kvelden før, bagger ut innen 22:00 så de leveres på pier.',
  9:  'Debarkering 06:00 — AVIS åpner 09:00, 757 Hornby. Frokost på Tim Hortons mens dere venter.',
  10: 'Hwy 5 Nord — fyll bensin før Hope, lite mellom Hope og Kamloops på lørdag.',
  11: 'Jasper: Athabasca Falls + Maligne Lake er 1t hver vei. Columbia Icefield krever forhåndsbooking.',
  12: 'Icefields Parkway — fyll bensin i Jasper, ingen stasjoner mellom Saskatchewan Crossing og Lake Louise.',
  13: 'Lake Louise: parkér i Lake Louise Village og ta shuttle opp — egen p-plass er full innen 08:00.',
  14: 'Moraine Lake åpner kl 06:00 via shuttle fra Lake Louise — solen treffer Ten Peaks ca 07:15.',
  15: 'Banff Gondola booker opp før kl 11. Reserver online før frokost.',
  16: 'Calgary — innlevering AVIS Airport. Sjekk drivstoffnivå før innlevering for å unngå 30 % surcharge.',
  17: 'Hjemreise — sjekk inn på Air Canada-app 24 t før. Trolley i Calgary er ofte forsinket fra LHR-stops.',
};

function row(label, value){
  return `<div class="fj-row"><dt>${esc(label)}</dt><dd>${value}</dd></div>`;
}

function renderHeartLine(){
  const h = getHeartState();
  if(h.count === 0){
    return `<a class="fj-row-link" href="/folg" target="_blank" rel="noopener">Del /folg-lenken →</a>`;
  }
  const newPart = h.newSinceSeen > 0
    ? ` <span class="fj-row-sub">· ${h.newSinceSeen} nye</span>`
    : '';
  const messages = (h.latestMessages || []).slice(0, 3);
  const msgBlock = messages.length
    ? `<div class="fj-heart-msgs">${messages.map(m => `<em>«${esc(m.text)}»</em>`).join('')}</div>`
    : '';
  return `<strong>${h.count} ♥</strong>${newPart} <a class="fj-row-link" href="/folg" target="_blank" rel="noopener">åpne</a>${msgBlock}`;
}

// N6 — Northern Lights-Kp-rad. Async-fyll inn etter render.
function renderAuroraLine(){
  return `<span id="fj-aurora-line" class="fj-row-sub">— laster …</span>`;
}

async function fillAuroraLine(){
  const el = document.getElementById('fj-aurora-line');
  if(!el) return;
  const out = await getAuroraOutlook();
  if(!out){ el.textContent = 'utilgjengelig'; return; }
  const kpStr = Number.isFinite(out.kp) ? ` <span class="fj-row-sub">· Kp ${out.kp.toFixed(1)}</span>` : '';
  el.innerHTML = `<span style="color:${out.visibility.color};font-weight:600">${out.visibility.glyph} ${out.visibility.label}</span>${kpStr}`;
}

async function fillWeatherLine(tripDay){
  const el = document.getElementById('fj-wx-line');
  if (!el || tripDay < 0) return;
  const w = await getDayWeather(tripDay);
  if (!w) {
    el.textContent = navigator.onLine ? 'Prognose åpner ~7 dager før dagen' : 'Ingen lagret prognose offline';
    return;
  }
  const staleTag = w.stale ? ` <span class="fj-row-sub" style="color:var(--amber,#C99A3B)">· ${ageLabel(w.ageMs)}</span>` : '';
  el.innerHTML = `${esc(formatBadge(w))} <span class="fj-row-sub">· ${esc(w.loc)} · ${w.tmin}–${w.tmax}°</span>${staleTag}`;
}

async function fillTideLine(tripDay){
  const el = document.getElementById('fj-tide-line');
  if (!el || !hasTideData(tripDay)) return;
  const tides = await getPortTides(tripDay);
  if (!tides) { el.textContent = 'utilgjengelig'; return; }
  el.innerHTML = formatTideLine(tides);
}

async function fillDailyTip(tripDay){
  const el = document.getElementById('fj-tip-body');
  if (!el || tripDay < 0) return;
  const tip = await getDailyTip(tripDay);
  if (tip) el.textContent = tip;
}

// #4 — valutalinje med staleness-merking. Viser cachet kurs umiddelbart,
// oppdaterer hvis nett er tilgjengelig. Aldri blank, aldri evig spinner.
async function fillRateLine(){
  const el = document.getElementById('fj-rate-line');
  if (!el) return;
  const cad = peekRates()['CAD-NOK'];
  el.innerHTML = `1 CAD = ${Number(cad).toFixed(2)} kr <span class="fj-row-sub">· henter …</span>`;
  const { rates, ageMs, stale } = await getRates();
  const usd = rates['USD-NOK'];
  const tag = stale ? ` <span class="fj-row-sub" style="color:var(--amber,#C99A3B)">· ${ageLabel(ageMs)}</span>` : '';
  el.innerHTML = `1 CAD = ${Number(rates['CAD-NOK']).toFixed(2)} kr <span class="fj-row-sub">· 1 USD = ${Number(usd).toFixed(2)} kr</span>${tag}`;
}

// Eksponert så main.js kan kalle den etter render
export function bindBriefAsync(tripDay){
  fillAuroraLine();
  fillRateLine();
  if (typeof tripDay === 'number' && tripDay >= 0){
    fillWeatherLine(tripDay);
    fillTideLine(tripDay);
    fillDailyTip(tripDay);
  }
  fillTimelineWeather();
}

function renderPreTripBrief(){
  const days = daysUntilDeparture();
  const milestone = days > 14
    ? `Online check-in åpner om ${days - 1} dager`
    : days > 1
      ? `Online check-in er åpen — sjekk Icelandair-appen`
      : `I morgen reiser dere!`;
  const tip = days > 7
    ? 'Tid for pass-sjekk: gyldighet minst 6 mnd etter hjemkomst (3. feb 2027).'
    : days > 3
      ? 'Last ned offline-kart i Google Maps for Vancouver, Banff og Skagway.'
      : 'Pakk dokumenter (pass, forsikring, AVIS-bekreftelse) i håndbagasje.';

  return `
  <section class="fj-brief">
    <header class="fj-brief-hdr">
      <h2>For <em>i dag</em></h2>
      <span class="fj-brief-meta">${days} dager til avreise</span>
    </header>

    <dl class="fj-rows">
      ${row('Status',     esc(milestone))}
      ${row('Avreise',    `17. juli <span class="fj-row-sub">· 13:50 OSL</span>`)}
    </dl>

    <aside class="fj-tip">
      <span class="fj-tip-lbl">Dagens tips</span>
      <p>${esc(tip)}</p>
    </aside>

    <dl class="fj-rows">
      ${renderPreflightRow()}
      ${row('Sjekkliste', `${getChecklistPct()} % klart`)}
      ${row('Reisefølge', 'Robert, Margita &amp; Nicholas')}
      ${row('Hjemmebanen', renderHeartLine())}
      ${row('Aurora',     renderAuroraLine())}
    </dl>

    <button class="fj-brief-ask" onclick="openQuickAsk()">Spør om noe annet …</button>
  </section>`;
}

// #6 — løft det mest presserende pre-flight-punktet inn i forside-briefen.
function renderPreflightRow(){
  const a = topPreflightAction();
  if (!a) return '';
  const when = a.overdue
    ? `<span style="color:var(--red);font-weight:700">på overtid</span>`
    : a.daysLeft === 0
      ? `<span style="color:var(--gold2);font-weight:700">frist i dag</span>`
      : `frist om ${a.daysLeft} d`;
  return row('Haster', `<a class="fj-row-link" onclick="setTab('klar','sjekk')" style="cursor:pointer">${esc(a.text)}</a> <span class="fj-row-sub">· ${when}</span>`);
}

function getChecklistPct(){
  try{
    // Total = alle items i PACKING (~58), uavhengig av lagret state
    const total = PACKING.reduce((s, g) => s + g.items.length, 0) || 1;
    const raw = localStorage.getItem('ca26pack');
    if(!raw) return 0;
    const items = JSON.parse(raw);
    const done = Object.values(items).filter(Boolean).length;
    return Math.round((done / total) * 100);
  }catch{ return 0; }
}

// Kort beskrivelse av dagens hovedaktivitet — første «innholdslinje» i planen.
function dayHighlight(day){
  const items = day.items || [];
  const main = items.find(i => /heldags|hele dagen/i.test(i.t || '')) || items[0];
  if (!main) return null;
  return main.v;
}

// Soltid-linje: oppgang/nedgang + gyllen time + ekte-mørke-flagg for aurora.
function renderSunLine(tripDay){
  const s = sunForTripDay(tripDay);
  if (!s) return '— laster …';
  if (s.midnightSun) return `<span style="color:#C99A3B;font-weight:600">☀ Midnattssol</span> <span class="fj-row-sub">· solen går ikke ned</span>`;
  const badge = esc(formatSunBadge(s));
  const golden = s.goldenEveningStart
    ? ` <span class="fj-row-sub">· gyllen time fra ${esc(s.goldenEveningStart)}</span>`
    : '';
  const dark = s.trueDark
    ? ` <span class="fj-row-sub">· mørkt ${esc(s.duskAstroStr || '')}–${esc(s.dawnAstroStr || '')}</span>`
    : ` <span class="fj-row-sub">· aldri helt mørkt</span>`;
  return `${badge}${golden}${dark}`;
}

function renderTripDayBrief(day, tripDay){
  const tip = day.phase === 'cruise' || day.phase === 'alaska'
    ? TIPS[day.phase]
    : TIPS[day.phase] || TIPS.city;
  const remind = REMINDERS[day.phase] || '—';
  const tipKey = ymd(new Date(Date.now()));
  const dayTip = getCachedTip(tipKey) || STATIC_TIPS[tripDay] || 'Nyt dagen — det viktigste står i planen.';
  setCachedTip(tipKey, dayTip);

  const tideRow = hasTideData(tripDay)
    ? row('Tidevann', `<span id="fj-tide-line" class="fj-row-sub">— laster …</span>`)
    : '';
  const highlight = dayHighlight(day);

  return `
  <section class="fj-brief">
    <header class="fj-brief-hdr">
      <h2>For <em>i dag</em></h2>
      <span class="fj-brief-meta">${esc(day.title)} · ${esc(day.d)}</span>
    </header>

    <dl class="fj-rows">
      ${highlight ? row('I dag', `${day.em || ''} ${esc(highlight)}`) : ''}
      ${row('Vær',    `<span id="fj-wx-line" class="fj-row-sub">— laster …</span>`)}
      ${row('Sollys', renderSunLine(tripDay))}
      ${tideRow}
      ${row('Aurora', renderAuroraLine())}
    </dl>

    <aside class="fj-tip">
      <span class="fj-tip-lbl">Dagens tips</span>
      <p id="fj-tip-body">${esc(dayTip)}</p>
    </aside>

    <dl class="fj-rows">
      ${row('Valuta',      `<span id="fj-rate-line" class="fj-row-sub">— laster …</span>`)}
      ${row('Tipping',     `${esc(tip.pct)} <span class="fj-row-sub">· ${esc(tip.note)}</span>`)}
      ${row('Husk',        esc(remind))}
      ${row('Hjemmebanen', renderHeartLine())}
    </dl>

    <button class="fj-brief-ask" onclick="openQuickAsk()">Spør om noe annet …</button>
  </section>`;
}

function renderOfflineStrip(){
  const off = getOfflineState();
  const online = navigator.onLine;
  const milestone = nextOfflineMilestone();
  const days = off.daysReady || 0;
  const ready = off.ready;
  const storage = formatStorage(off);

  const state = !ready ? 'pending' : (online ? 'ready' : 'offline');
  const label = !ready
    ? 'Pakker for offline …'
    : online
      ? `Klar for offline · ${days} dager lastet`
      : `Du er offline · ${days} dager tilgjengelig`;
  const meta = milestone
    ? `${milestone.icon} ${milestone.label}`
    : storage;

  return `
  <div class="fj-offline-strip fj-offline-${state}" role="status">
    <span class="fj-offline-dot" aria-hidden="true"></span>
    <span class="fj-offline-label">${esc(label)}</span>
    <span class="fj-offline-meta">${esc(meta)}</span>
    <button class="fj-offline-pack" onclick="window.precacheNextLeg()" aria-label="Forhåndslast neste etappe">Pakk</button>
  </div>`;
}

export function renderBrief(tripDay){
  return renderOfflineStrip()
    + (tripDay < 0 ? renderPreTripBrief() : renderTripDayBrief(DAYS[tripDay], tripDay));
}
